import { Box, FormHelperText } from '@mui/material';
import { FormNumberField, FormSelect, FormSelectOption, FormTextField } from 'components/forms';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { Control, DeepMap, FieldError, RegisterOptions, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { COMPETITOR_OPTIONS } from 'shared/constants';
import { useCompetitorProductOptions } from 'shared/hooks';
import { useGetEndUserPricingGroupDropdown } from 'shared/hooks/useGetEndUserPricingGroupDropdown';
import { isPositiveOrZero } from 'shared/utility/dataGrid';
import { CompetitorProductDropdown, EndUserDropdown, EndUserPricingGroupDropdown } from 'types/backend';
import { AddModelCurrentPricesForm } from 'types/forms';
import { EffectiveDate } from './effectiveDate';
import { ExpirationDate } from './expirationDate';
import { OneTimeOnly } from './oneTimeOnly';

type AddDeviationFormProps = {
    index: number;
    control: Control<AddModelCurrentPricesForm>;
    errors: DeepMap<AddModelCurrentPricesForm, FieldError> | undefined;
    endUserDropdownOptions: FormSelectOption<EndUserDropdown>[];
    isEndUserDropdownLoading: boolean;
    watch?: UseFormWatch<AddModelCurrentPricesForm>;
    setValue?: UseFormSetValue<AddModelCurrentPricesForm>;
};

export type IAddDeviationFormChildrenProps = Pick<AddDeviationFormProps, 'index' | 'control' | 'watch' | 'setValue'> & {
    rules?: Omit<RegisterOptions<AddModelCurrentPricesForm>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
    fieldError?: FieldError;
    errors?: DeepMap<AddModelCurrentPricesForm, FieldError>;
};

export const addDeviationFormRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0,1fr))',
    gridTemplateRows: '1',
    width: '100%',
    gap: 2,
    padding: `1rem`,
};

export default function AddDeviationForm({
    index,
    control,
    errors,
    endUserDropdownOptions,
    isEndUserDropdownLoading,
    watch,
    setValue,
}: AddDeviationFormProps) {
    const params = useParams<AccountNumberParams>();
    const competitorOption = watch ? watch(`addedDeviations.${index}.modelCurrentPrice.competitor`) : '';
    const deviationType = watch ? watch(`addedDeviations.${index}.deviationDetails.deviationType`) : '';

    const competitorRequiredRule = { required: '' };
    const competitorProductRequiredRule = { required: '' };
    const competitorProductPriceRequiredRule = { required: '' };
    const deviationPriceRequiredRule = { required: 'New deviated price is required', validate: isPositiveOrZero };
    const estAnnualPriceRequiredRule = { required: '' };
    const deviationNotesRequiredRule = { required: 'Deviation reason notes are required' };

    const {
        data: competitorProductDropdownResponse,
        isLoading: competitorProductsIsLoading,
        isFetching: competitorProductsIsFetching,
    } = useCompetitorProductOptions(competitorOption);

    const endUserPricingGroupDropdownResponse = useGetEndUserPricingGroupDropdown(params.accountNumber);
    const isEndUserPricingGroupDropdownLoading =
        endUserPricingGroupDropdownResponse.isLoading || endUserPricingGroupDropdownResponse.isFetching;
    const endUserPricingGroupDropdownOptions: FormSelectOption<EndUserPricingGroupDropdown>[] =
        endUserPricingGroupDropdownResponse.data?.rows
            ?.map((row) => {
                return {
                    value: row.endUserPricingGroup,
                    displayText: row.endUserPricingGroup,
                    original: row,
                };
            })
            .sort((a, b) => a.displayText.localeCompare(b.displayText)) || [];

    const competitorProductDropdownOptions: FormSelectOption<CompetitorProductDropdown>[] =
        competitorProductDropdownResponse?.map((competitorProduct) => {
            return {
                key: competitorProduct.competitorProductsId,
                value: competitorProduct.competitorProduct,
                displayText: competitorProduct.competitorProduct,
                original: competitorProduct,
            };
        }) || [];

    const isCompetitorProductDropdownLoading = competitorProductsIsLoading || competitorProductsIsFetching;

    return (
        <>
            <Box sx={addDeviationFormRowStyle}>
                <Box sx={{ gridColumn: 1 }}>
                    <FormSelect
                        name={`addedDeviations.${index}.deviationDetails.deviationType`}
                        label='Deviation Type'
                        control={control}
                        rules={{
                            required: 'Deviation Type is required',
                            validate: {
                                noAvailablePricingGroups: (value) =>
                                    value === 'Pricing Group' &&
                                        !endUserPricingGroupDropdownOptions.length &&
                                        !isEndUserPricingGroupDropdownLoading
                                        ? 'No available pricing groups'
                                        : undefined,
                            },
                        }}
                        options={[
                            { value: 'End User', displayText: 'End User' },
                            { value: 'Pricing Group', displayText: 'Pricing Group' },
                        ]}
                        fieldError={errors?.['addedDeviations']?.[index]?.['deviationDetails']?.['deviationType']}
                    />
                    <FormHelperText error>
                        {errors?.['addedDeviations']?.[index]?.['deviationDetails']?.['deviationType']?.['message']}
                    </FormHelperText>
                </Box>
                {deviationType === 'Pricing Group' ? (
                    <Box sx={{ gridColumn: 2 }}>
                        <FormSelect
                            name={`addedDeviations.${index}.modelCurrentPrice.enduserpricinggroup`}
                            label='Pricing Group'
                            control={control}
                            options={endUserPricingGroupDropdownOptions}
                            isLoading={isEndUserPricingGroupDropdownLoading}
                            disabled={!endUserPricingGroupDropdownOptions.length}
                            rules={{ required: 'Pricing Group is required' }}
                            watch={watch}
                            fieldError={
                                errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['enduserpricinggroup']
                            }
                        />
                        <FormHelperText error>
                            {errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['enduserpricinggroup']?.[
                                'message'
                            ] || !endUserPricingGroupDropdownOptions.length
                                ? 'No available pricing groups'
                                : undefined}
                        </FormHelperText>
                    </Box>
                ) : (
                    <Box sx={{ gridColumn: 2 }}>
                        <FormSelect
                            name={`addedDeviations.${index}.modelCurrentPrice.enduseraccountnumber`}
                            label='End User'
                            control={control}
                            options={endUserDropdownOptions}
                            isLoading={isEndUserDropdownLoading}
                            rules={{ required: 'End User or Group is required' }}
                            watch={watch}
                            sideEffectHandler={() => {
                                const watchedAccountNumber = watch
                                    ? watch(`addedDeviations.${index}.modelCurrentPrice.enduseraccountnumber`)
                                    : '';
                                const endUserDropdownItem = endUserDropdownOptions.find((endUserDropdownOption) => {
                                    return endUserDropdownOption.value === watchedAccountNumber;
                                });
                                if (endUserDropdownItem?.original && setValue) {
                                    setValue(
                                        `addedDeviations.${index}.endUserDisplay`,
                                        endUserDropdownItem.original.endusername
                                    );
                                    setValue(`addedDeviations.${index}.street`, endUserDropdownItem.original.street);
                                    setValue(`addedDeviations.${index}.city`, endUserDropdownItem.original.city);
                                    setValue(`addedDeviations.${index}.state`, endUserDropdownItem.original.state);
                                }
                            }}
                            fieldError={
                                errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['enduseraccountnumber']
                            }
                        />
                        <FormHelperText error>
                            {
                                errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['enduseraccountnumber']?.[
                                'message'
                                ]
                            }
                        </FormHelperText>
                    </Box>
                )}
                <EffectiveDate index={index} control={control} watch={watch} setValue={setValue} errors={errors} />
                <ExpirationDate index={index} control={control} errors={errors} />
                <OneTimeOnly index={index} control={control} watch={watch} setValue={setValue} />
            </Box>
            <Box sx={addDeviationFormRowStyle}>
                <Box sx={{ gridColumn: 1 }}>
                    <FormNumberField
                        name={`addedDeviations.${index}.modelCurrentPrice.price`}
                        label='New Deviated Price'
                        control={control}
                        rules={deviationPriceRequiredRule}
                        fieldError={errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['price']}
                        type='money'
                    />
                </Box>
                <Box sx={{ gridColumn: 2 }}>
                    <FormSelect
                        name={`addedDeviations.${index}.deviationDetails.deviationReason`}
                        label='Deviation Reason'
                        control={control}
                        rules={{ required: 'Deviation Reason is required' }}
                        options={[
                            { value: 'Volume Growth', displayText: 'Volume Growth' },
                            { value: 'Competition', displayText: 'Competition' },
                            { value: 'Other', displayText: 'Other' },
                        ]}
                        fieldError={errors?.['addedDeviations']?.[index]?.['deviationDetails']?.['deviationReason']}
                    />
                    <FormHelperText error>
                        {errors?.['addedDeviations']?.[index]?.['deviationDetails']?.['deviationReason']?.['message']}
                    </FormHelperText>
                </Box>
                <Box sx={{ gridColumn: 3 }}>
                    <FormNumberField
                        name={`addedDeviations.${index}.modelCurrentPrice.estmonthlyrevenue`}
                        label='Est. Annual Volume'
                        control={control}
                        rules={estAnnualPriceRequiredRule}
                        fieldError={errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['estmonthlyrevenue']}
                        type='number'
                    />
                </Box>
                <Box sx={{ gridColumn: 4 }}>
                    <FormSelect
                        name={`addedDeviations.${index}.modelCurrentPrice.competitor`}
                        label='Competitor'
                        control={control}
                        rules={competitorRequiredRule}
                        options={COMPETITOR_OPTIONS}
                        fieldError={errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['competitor']}
                    />
                    <FormHelperText error>
                        {errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['competitor']?.['message']}
                    </FormHelperText>
                </Box>
                <Box sx={{ gridColumn: 5 }}>
                    <FormNumberField
                        name={`addedDeviations.${index}.modelCurrentPrice.competitorprice`}
                        label='Competitor Price'
                        control={control}
                        rules={competitorProductPriceRequiredRule}
                        fieldError={errors?.['addedDeviations']?.[index]?.['modelCurrentPrice']?.['competitorprice']}
                        type='money'
                    />
                </Box>
                <Box sx={{ gridColumn: 1 }}>
                    <FormSelect
                        name={`addedDeviations.${index}.competitorProduct`}
                        label='Competitor Product'
                        control={control}
                        options={competitorProductDropdownOptions}
                        isLoading={isCompetitorProductDropdownLoading}
                        rules={competitorProductRequiredRule}
                        watch={watch}
                        fieldError={errors?.['addedDeviations']?.[index]?.['competitorProduct']}
                    />
                    <FormHelperText error>
                        {errors?.['addedDeviations']?.[index]?.['competitorProduct']?.message}
                    </FormHelperText>
                </Box>
                <Box sx={{ gridColumn: 2 }}>
                    <FormTextField
                        name={`addedDeviations.${index}.deviationReasonNotes`}
                        label={'Deviation Reason Notes'}
                        control={control}
                        rules={deviationNotesRequiredRule}
                        fieldError={errors?.['addedDeviations']?.[index]?.['deviationReasonNotes']}
                    />
                </Box>
            </Box>
        </>
    );
}
